import { UseMutationResult, UseQueryResult, useMutation, useQuery } from '@tanstack/react-query'
import { throwIfCutShort, useFetchAllRows, UseFetchAllRowsResult } from '@/hooks/useFetchAllRows'
import { cancelInbound, getInboundDetails, getInbounds } from './apis'
import { INBOUNDS_FETCH_SIZE } from './consts'
import { CancelInboundReq, GetInboundDetailsItem, GetInboundDetailsReq, GetInboundsItem, GetInboundsReq } from './types'

/**
 * 쿼리 키 앞머리에 라우트 메뉴코드(gr09 — placeholder, D10)를 둔다.
 * `MenuOnSessionWrap.remove()` 가 탭을 닫을 때 키에 이 코드가 든 캐시를 함께 지운다.
 */
const PATH = ['inbound', 'inboundInquiry', 'gr09'] as const

/** HEAD 조회 조건 — BE 구간(page·size)은 공용 훅이 채운다. 키에 넣지 않아 페이지를 넘겨도 다시 받지 않는다. */
export type InboundsCondition = Omit<GetInboundsReq, 'page' | 'size'>

export const inboundInquiryQueryKeys = {
  all: PATH,
  head: (condition: InboundsCondition | null) => [...PATH, 'HEAD', condition] as const,
  item: (params: GetInboundDetailsReq | null) => [...PATH, 'ITEM', params] as const,
  /** 화주 목록 — 화면 필터 옵션 및 세션 화주의 서비스유형 조회에 쓴다. */
  shippers: (scope: string) => [...PATH, 'SHIPPERS', scope] as const,
}

/**
 * 구간 경계에서 겹친 행을 거르는 키 — HEAD 는 행 PK 가 없고 BE 가 화주·입고번호·마감일·차수 4개로 묶는다(GROUP BY).
 * 화주를 빼면 화주만 다른 두 행 중 하나가 조용히 사라진다(뷰 선택용 `__pick` 은 3필드 그대로).
 */
const headKeyOf = (row: GetInboundsItem): string => `${row.shipperCode}|${row.ordOptionNumber}|${row.clsDate}|${row.cutSeq}`

/**
 * HEAD 조회 — 전량 조회(LOGISPRD-15268). BE 상한(1,000)씩 끊어 받고 화면은 10,000건씩 나눈다.
 * `condition` 이 없으면 조회하지 않는다 — 진입 즉시 조회하지 않고 [조회]·복원으로 조건이 서야 받는다(GR05 준용).
 * 실패 안내는 공용 훅의 전역 알럿(서버 meta.message) 하나다 — 화면이 따로 띄우지 않는다.
 */
export const useGetInbounds = (condition: InboundsCondition | null): UseFetchAllRowsResult<GetInboundsItem> =>
  useFetchAllRows({
    queryKey: inboundInquiryQueryKeys.head(condition),
    fetchPage: async (page, size = INBOUNDS_FETCH_SIZE) => {
      // enabled 가 condition !== null 이라 null 로는 불리지 않는다(단언 대신 spread — null 이면 조건 없는 요청이 아니라 훅이 끈다)
      const res = await getInbounds({ ...condition, page, size })
      // pageInfo 가 빠진 응답을 0건으로 메우지 않는다 — 없으면 여기서 던져 실패 알럿으로 드러낸다(rgr13 선례).
      const { items, pageInfo } = res.data
      throwIfCutShort(page, size, items.length, pageInfo.totalCount)
      return { items, totalCount: pageInfo.totalCount }
    },
    chunkSize: INBOUNDS_FETCH_SIZE,
    getKey: headKeyOf,
    enabled: condition !== null,
  })

/**
 * ITEM 조회. `params` 가 `null` 이면 호출하지 않는다 — HEAD 행 미선택, 또는 `cutSeq` 가 없는 행이라
 * 호출부가 조회 자체를 막은 경우다(계획 5단계·R-09). 조회 조건이 바뀌면 새 키로 갈리므로 이전 결과를 들고 있지 않는다.
 */
export const useGetInboundDetails = (params: GetInboundDetailsReq | null): UseQueryResult<GetInboundDetailsItem[], Error> =>
  useQuery({
    queryKey: inboundInquiryQueryKeys.item(params),
    queryFn: async () => {
      const res = await getInboundDetails(params as GetInboundDetailsReq)
      return res.data?.items ?? []
    },
    // 화면이 error 를 소비해 알럿을 따로 띄우지 않는다 — meta.noErrorMessage 를 주지 않고 전역 QueryCache.onError 에 그대로 위임한다.
    enabled: params !== null,
  })

/**
 * 입고취소 단건 요청. 다중 선택 순차 호출·부분 실패 집계는 화면 훅(useInboundInquiryScreen)이 맡는다 —
 * 이 mutation 은 한 건을 보내고 성공·실패를 그대로 돌려줄 뿐이다.
 */
export const useCancelInbound = (): UseMutationResult<void, Error, CancelInboundReq> =>
  useMutation({
    mutationFn: async (params: CancelInboundReq): Promise<void> => {
      await cancelInbound(params, 'GR09')
    },
  })
